// Planetary Power-Split (eCVT) Calculator: defaults, field metadata, client-side
// validation, vehicle presets, driving-mode scenarios and the plain-language block
// descriptions for the architecture diagram. The physics and authoritative validation live
// server-side -- see src/lib/toolsApi.js (powerSplitToolApi) and
// backend/tools/power_split_tool.py. Field bounds, defaults, presets and driving modes
// mirror backend/core/lookup/power_split_specs.py exactly (checked by a backend test).

export const DEFAULT_INPUTS = {
  mode: 'ths',
  sunTeeth: 30,
  ringTeeth: 78,
  pinionTeeth: null,
  planetCount: 4,
  meshEffPct: 100,
  unknownMember: 'sun',
  sunRpm: 3300,
  carrierRpm: 2000,
  ringRpm: 1500,
  torqueMember: 'carrier',
  torqueNm: 100,
  engineRpm: 2000,
  engineTorqueNm: 100,
  speedBasis: 'ring',
  vehicleSpeedKmh: 50,
  mg2Mode: 'battery',
  batteryTargetKw: 0,
  mg2TorqueNm: 50,
  mg2Ratio: 2.636,
  finalDriveRatio: 3.268,
  tyreRadiusM: 0.308,
  motorEffPct: 100,
  inverterEffPct: 100,
  boostFitted: true,
  mg1MaxRpm: 10000,
  engineMaxRpm: 5000,
  pinionMaxRpm: 10000,
  sweepMaxKmh: 180,
};

export const INTEGER_KEYS = ['sunTeeth', 'ringTeeth', 'pinionTeeth', 'planetCount'];
export const MAX_PROFILE_SHIFT_TEETH = 3;

export const FIELD_SPECS = {
  sunTeeth: { key: 'sunTeeth', label: 'Sun Teeth (Zs)', unit: '', min: 10, max: 100, strictlyPositive: true },
  ringTeeth: { key: 'ringTeeth', label: 'Ring Teeth (Zr)', unit: '', min: 20, max: 250, strictlyPositive: true },
  planetCount: { key: 'planetCount', label: 'Planets (N)', unit: '', min: 2, max: 8, strictlyPositive: true },
  meshEffPct: { key: 'meshEffPct', label: 'Mesh Efficiency', unit: '%', min: 90, max: 100, strictlyPositive: true },
  pinionMaxRpm: { key: 'pinionMaxRpm', label: 'Pinion Max (rel.)', unit: 'rpm', min: 100, max: 50000, strictlyPositive: true },
  pinionTeeth: { key: 'pinionTeeth', label: 'Pinion Teeth (Zp)', unit: '', min: 8, max: 120, strictlyPositive: true, optional: true },
  sunRpm: { key: 'sunRpm', label: 'Sun Speed', unit: 'rpm', min: -30000, max: 30000, strictlyPositive: false },
  carrierRpm: { key: 'carrierRpm', label: 'Carrier Speed', unit: 'rpm', min: -30000, max: 30000, strictlyPositive: false },
  ringRpm: { key: 'ringRpm', label: 'Ring Speed', unit: 'rpm', min: -30000, max: 30000, strictlyPositive: false },
  torqueNm: { key: 'torqueNm', label: 'Known Torque', unit: 'N·m', min: -10000, max: 10000, strictlyPositive: false },
  engineRpm: { key: 'engineRpm', label: 'Engine Speed', unit: 'rpm', min: -3000, max: 12000, strictlyPositive: false },
  engineTorqueNm: { key: 'engineTorqueNm', label: 'Engine Torque', unit: 'N·m', min: -200, max: 1000, strictlyPositive: false },
  motorEffPct: { key: 'motorEffPct', label: 'MG1 / MG2 Efficiency', unit: '%', min: 70, max: 100, strictlyPositive: true },
  inverterEffPct: { key: 'inverterEffPct', label: 'Inverter Efficiency', unit: '%', min: 70, max: 100, strictlyPositive: true },
  mg2Ratio: { key: 'mg2Ratio', label: 'MG2 Reduction Ratio', unit: ':1', min: 1.0, max: 6.0, strictlyPositive: true },
  mg1MaxRpm: { key: 'mg1MaxRpm', label: 'MG1 Max Speed', unit: 'rpm', min: 1000, max: 30000, strictlyPositive: true },
  engineMaxRpm: { key: 'engineMaxRpm', label: 'Engine Max Speed', unit: 'rpm', min: 1000, max: 12000, strictlyPositive: true },
  sweepMaxKmh: { key: 'sweepMaxKmh', label: 'Sweep Max Speed', unit: 'km/h', min: 20, max: 300, strictlyPositive: true },
  vehicleSpeedKmh: { key: 'vehicleSpeedKmh', label: 'Vehicle Speed', unit: 'km/h', min: -60, max: 300, strictlyPositive: false },
  mg2TorqueNm: { key: 'mg2TorqueNm', label: 'MG2 Torque', unit: 'N·m', min: -1000, max: 1000, strictlyPositive: false },
  batteryTargetKw: { key: 'batteryTargetKw', label: 'Battery Power', unit: 'kW', min: -300, max: 300, strictlyPositive: false },
  finalDriveRatio: { key: 'finalDriveRatio', label: 'Ring-to-Axle Ratio', unit: ':1', min: 1.0, max: 12.0, strictlyPositive: true },
  tyreRadiusM: { key: 'tyreRadiusM', label: 'Tyre Rolling Radius', unit: 'm', min: 0.15, max: 0.6, strictlyPositive: true },
};

export const GEAR_KEYS = ['sunTeeth', 'ringTeeth', 'pinionTeeth', 'planetCount', 'meshEffPct', 'pinionMaxRpm'];
export const THS_KEYS = ['engineRpm', 'engineTorqueNm', 'motorEffPct', 'inverterEffPct', 'mg2Ratio', 'mg1MaxRpm', 'engineMaxRpm', 'sweepMaxKmh'];
export const LINK_KEYS = ['finalDriveRatio', 'tyreRadiusM'];
export const MEMBER_SPEED_KEY = { sun: 'sunRpm', carrier: 'carrierRpm', ring: 'ringRpm' };

export const MEMBERS = [
  { value: 'sun', label: 'Sun (S)' },
  { value: 'carrier', label: 'Carrier (C)' },
  { value: 'ring', label: 'Ring (R)' },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return spec.optional ? null : 'Required.';
  const value = Number(rawValue);
  if (Number.isNaN(value)) return 'Must be a number.';
  if (spec.strictlyPositive && value <= 0) return 'Must be greater than 0.';
  if (spec.min !== null && value < spec.min) return `Must be ≥ ${spec.min}.`;
  if (spec.max !== null && value > spec.max) return `Must be ≤ ${spec.max}.`;
  if (INTEGER_KEYS.includes(spec.key) && Math.abs(value - Math.round(value)) > 1e-9) return 'Must be a whole number.';
  return null;
}

// Gear-set cross-field rules (mirrors _validate_gear_set in the backend).
export function validateGearSet(values, errors) {
  const { sunTeeth: zs, ringTeeth: zr, pinionTeeth: zp } = values;
  if (errors.sunTeeth || errors.ringTeeth || zs === undefined || zr === undefined) return;
  if (zr <= zs) {
    errors.ringTeeth = 'Must be greater than the sun tooth count (Zr > Zs).';
    return;
  }
  const standard = (zr - zs) / 2;
  if (zp === null || zp === undefined) {
    if ((zr - zs) % 2) {
      errors.gearSet = `Zr − Zs = ${zr - zs} is odd, so the standard pinion (Zr − Zs)/2 = ${standard} is not a whole number. `
        + 'Change Zs or Zr, or enter the pinion tooth count for a profile-shifted set.';
    }
  } else if (!errors.pinionTeeth && Math.abs(zp - standard) > MAX_PROFILE_SHIFT_TEETH) {
    errors.pinionTeeth = `Must be within ±${MAX_PROFILE_SHIFT_TEETH} teeth of (Zr − Zs)/2 = ${standard} for the planets to mesh with both the sun and the ring.`;
  }
}

// Vehicle presets: gear data + rolling radius only. Sources are listed in
// backend/core/lookup/power_split_specs.py and in the user guide.
export const PRESETS = {
  prius_gen2: {
    label: 'Prius Gen 2 (THS II)',
    values: { sunTeeth: 30, ringTeeth: 78, pinionTeeth: 23, planetCount: 4, mg2Ratio: 1.0, finalDriveRatio: 4.113, tyreRadiusM: 0.301, boostFitted: true },
  },
  prius_gen3: {
    label: 'Prius Gen 3 (THS II, MG2 reduction)',
    values: { sunTeeth: 30, ringTeeth: 78, pinionTeeth: 23, planetCount: 4, mg2Ratio: 2.636, finalDriveRatio: 3.268, tyreRadiusM: 0.308, boostFitted: true },
  },
};

export const PRESET_NOTES = {
  prius_gen2: 'Sun 30 / pinion 23 / ring 78, 4 planets. MG2 on the ring with no reduction; ring-to-axle 4.113 (ORNL 2004 benchmark). 185/65R15 tyre → 0.301 m rolling radius (derived).',
  prius_gen3: 'Sun 30 / pinion 23 / ring 78, 4 planets. MG2 through a 58/22 = 2.636 reduction planetary; ring-to-axle (77/24)·(55/54) = 3.268 (ORNL 2010 benchmark). 195/65R15 → 0.308 m (derived).',
};

// Driving-mode explorer scenarios: fill the operating point only (gear data and limits stay).
export const DRIVING_MODES = {
  ev: {
    label: 'EV drive',
    values: { engineRpm: 0, engineTorqueNm: 0, speedBasis: 'vehicle', vehicleSpeedKmh: 40, mg2Mode: 'torque', mg2TorqueNm: 60 },
  },
  engineStart: {
    label: 'Engine start',
    values: { engineRpm: 1000, engineTorqueNm: -40, speedBasis: 'vehicle', vehicleSpeedKmh: 0, mg2Mode: 'torque', mg2TorqueNm: 0 },
  },
  cruise: {
    label: 'Cruise / power split',
    values: { engineRpm: 1800, engineTorqueNm: 90, speedBasis: 'vehicle', vehicleSpeedKmh: 60, mg2Mode: 'battery', batteryTargetKw: 0 },
  },
  charging: {
    label: 'Charging while driving',
    values: { engineRpm: 2000, engineTorqueNm: 110, speedBasis: 'vehicle', vehicleSpeedKmh: 50, mg2Mode: 'torque', mg2TorqueNm: 0 },
  },
  acceleration: {
    label: 'Full acceleration',
    values: { engineRpm: 4000, engineTorqueNm: 130, speedBasis: 'vehicle', vehicleSpeedKmh: 80, mg2Mode: 'battery', batteryTargetKw: 20 },
  },
  regen: {
    label: 'Regenerative braking',
    values: { engineRpm: 0, engineTorqueNm: 0, speedBasis: 'vehicle', vehicleSpeedKmh: 60, mg2Mode: 'torque', mg2TorqueNm: -80 },
  },
  reverse: {
    label: 'Reverse (MG2 only)',
    values: { engineRpm: 0, engineTorqueNm: 0, speedBasis: 'vehicle', vehicleSpeedKmh: -10, mg2Mode: 'torque', mg2TorqueNm: -60 },
  },
};

export const DRIVING_MODE_TEXT = {
  ev: {
    summary: 'Engine off, MG2 alone drives the wheels from the battery.',
    watch: 'Carrier (engine) sits at 0 rpm, so the lever pivots there: the sun (MG1) spins backwards at (Zr/Zs) × ring speed with no load.',
  },
  engineStart: {
    summary: 'Car stopped; MG1 cranks the engine through the planetary.',
    watch: 'With the ring held at 0 rpm, MG1 motoring forwards drives the carrier, which spins the engine up. The battery pays for it. The ring sees a small backward reaction torque, held by the brakes (MG2 can also hold it).',
  },
  cruise: {
    summary: 'Engine drives; its power splits into a mechanical path and an electrical path.',
    watch: 'MG1 generates (reaction torque on the sun) and all of that electricity goes to MG2. Battery power is zero; mechanical + electrical path = 100 % of engine power.',
  },
  charging: {
    summary: 'Engine makes more power than the wheels need; the surplus charges the battery.',
    watch: 'MG1 generates, MG2 is unloaded, so MG1\'s power flows into the battery.',
  },
  acceleration: {
    summary: 'Engine at high power and the battery adds 20 kW through MG2.',
    watch: 'MG2 uses MG1\'s electricity plus battery power, so the wheels get more than the engine alone delivers.',
  },
  regen: {
    summary: 'Engine off; the wheels drive MG2 as a generator to charge the battery.',
    watch: 'MG2 torque opposes motion (negative torque at positive speed). MG1 freewheels backwards as in EV mode.',
  },
  reverse: {
    summary: 'Engine off; MG2 turns backwards. There is no reverse gear.',
    watch: 'Ring and MG2 speeds are negative; MG2 torque is negative too, so its power is positive (motoring). The sun spins forwards.',
  },
};

// Plain-language "what it does" text for each architecture-diagram block.
export const BLOCK_INFO = {
  engine: { name: 'Engine (ICE)', text: 'The petrol engine. In THS it is bolted (through the damper) to the planet carrier, so engine speed = carrier speed. Its speed is set by MG1, not by vehicle speed.' },
  damper: { name: 'Torsional damper', text: 'A spring damper between the engine and the gear set that smooths out the engine\'s firing pulses. It passes speed and torque straight through.' },
  psd: { name: 'Planetary gear set (power-split device)', text: 'Sun, planet carrier and ring gear. Engine on the carrier, MG1 on the sun, output on the ring. It splits engine torque in the fixed ratio Zs : Zr between the sun and the ring.' },
  mg1: { name: 'MG1 (Motor-Generator 1)', text: 'Connected to the sun gear. Usually a generator: its reaction torque holds the sun so engine torque reaches the ring, and its speed sets the engine speed. It also starts the engine.' },
  mg2: { name: 'MG2 (Motor-Generator 2)', text: 'The traction motor, geared to the ring/output. It drives the wheels in EV mode, adds torque when accelerating, and is the generator during regenerative braking.' },
  reduction: { name: 'MG2 reduction gear', text: 'Gears MG2 down to the ring so a smaller, faster motor can give more torque (Gen 3: 2.636:1). Ratio 1:1 means MG2 sits directly on the ring (Gen 2).' },
  counter: { name: 'Counter gear / final drive', text: 'Fixed gears from the ring (output) down to the differential. Ring-to-axle ratio links ring rpm to wheel rpm.' },
  diff: { name: 'Differential', text: 'Splits the drive between the left and right wheels and lets them turn at different speeds in corners.' },
  wheels: { name: 'Wheels', text: 'The total power arriving here is the ring\'s mechanical share plus MG2\'s power.' },
  inverter: { name: 'Inverter', text: 'Converts DC from the battery bus to three-phase AC for MG1 and MG2, and back. Power generated by MG1 can flow straight through it to MG2 without touching the battery.' },
  boost: { name: 'Boost converter', text: 'Raises the battery voltage to a higher DC bus voltage for the motors (Prius Gen 2/3: up to 500 / 650 V). Greyed out when not fitted: the battery then feeds the inverter directly. Modelled as lossless.' },
  battery: { name: 'HV battery', text: 'Supplies or absorbs the difference between MG1 and MG2 electrical power: P_batt = P_MG1,elec + P_MG2,elec (positive = discharging).' },
};

export const STATE_LABELS = {
  motoring: 'Motoring',
  generating: 'Generating',
  idle: 'Idle',
  driving: 'Driving',
  driven: 'Driven',
  braking: 'Braking',
  motored: 'Being driven',
  stopped: 'Stopped',
  spinning: 'Spinning, no torque',
  discharging: 'Discharging',
  charging: 'Charging',
};
